import { useEffect, useMemo, useState } from "react"
import { EditableFormField, EditablePanel, FieldType, FieldValue, O, option, SelectField, TabsPanel, UIPermission } from "../../../../features/ui"
import { useActions } from "./actions"
import { useSalesHooks } from "../../hooks/salesHooks"
import { NegativeStockPanel, Product } from "../../../products"
import { ModalId, useUIActions } from "../../../../features/ui"
import { NonHoardableProductsTable } from "../hoard/non-hoardable-products-table/nonHoardableProductsTable"
import { TableName } from "../../../../tables"
import { NonHoardableProductFormModal } from "../hoard/non-hoardable-product-form-modal/nonHoardableProductFormModal"
import { UnignoreProductModal } from "../hoard/unignore-product-modal/unignoreProductModal"
import { useSystemStateHooks } from "../../../system"
import { DocumentFooters, DocumentKey, DocumentPaymentTypeIds } from "../../../administration"
import { Labels } from "../../../../features/data-types"
import { DocumentPaymentTypeIdsFields } from './documentPaymentTypeIdsFields'
import { DEFAULT_CHANGE_MULTIPLE } from '../../constants/payments'

export const SalesSettingsPanel = () => {
    const { getSettings } = useSalesHooks()
    const settings = getSettings()

    const documentFooters = settings?.documentFooters as O<DocumentFooters>
    const documentPaymentTypeIds = settings?.documentPaymentTypeIds as O<DocumentPaymentTypeIds>
    const generalHoardTermMonths = useMemo(() => settings?.generalHoardTermMonths as O<number>, [settings])
    const hoardIgnoredProductIds = useMemo(() => (settings?.hoardIgnoredProductIds || []) as string[], [settings])
    const changeMultiple = useMemo(() => (settings?.changeMultiple as O<number>) || DEFAULT_CHANGE_MULTIPLE, [settings])
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_SALES_SETTINGS)

    const [documentKey, setDocumentKey] = useState<O<DocumentKey>>()
    const [documentFooterState, setDocumentFooterState] = useState<string>('')
    const [documentPaymentTypeIdsFieldKey, setDocumentPaymentTypeIdsFieldKey] = useState<number>(0)
    const [generalHoardTermMonthsState, setGeneralHoardTermMonthsState] = useState<O<number>>(generalHoardTermMonths)
    const [hoardIgnoredProductIdsState, setHoardIgnoredProductIdsState] = useState<string[]>(hoardIgnoredProductIds)
    const [changeMultipleState, setChangeMultipleState] = useState<number>(changeMultiple)
    const [currentProductId, setCurrentProductId] = useState<string>()

    const { updateDocumentFooter, submitDocumentPaymentTypeIds, submitGeneralHoardTermMonths, submitChangeMultiple, submitIgnoreProduct, submitUnignoreProduct } = useActions()
    const { toggleModal } = useUIActions()

    useEffect(() => setDocumentPaymentTypeIdsFieldKey(prev => prev + 1), [documentKey])

    useEffect(() => {
        setDocumentFooterState(documentFooters && documentKey ? documentFooters[documentKey] : '')
    }, [documentKey, documentFooters])

    useEffect(() => setHoardIgnoredProductIdsState(hoardIgnoredProductIds), [hoardIgnoredProductIds])

    const documentKeyOptions = useMemo(() => Object.values(DocumentKey).map(key => option(key, Labels.documentKeyLabel(key))), [])
    const ignoredProductsData = hoardIgnoredProductIdsState.map(productId => ({ productId }))
    const filterProducts = (product: Product) => currentProductId === product.id || !hoardIgnoredProductIdsState.includes(product.id)

    const onChangeDocumentKey = (value?: FieldValue) => setDocumentKey(value as O<DocumentKey>)

    const onSaveDocumentFooter = (value?: FieldValue) => {
        const newDocumentFooter = value! as string
        if (newDocumentFooter !== documentFooterState) {
            setDocumentFooterState(newDocumentFooter)
            updateDocumentFooter(documentKey!, newDocumentFooter)
        }
    }

    const onSaveGeneralSaleYield = (value?: FieldValue) => {
        const newGeneralHoardTermMonths = value as O<number>
        if (newGeneralHoardTermMonths && newGeneralHoardTermMonths !== generalHoardTermMonthsState) {
            setGeneralHoardTermMonthsState(newGeneralHoardTermMonths)
            submitGeneralHoardTermMonths(newGeneralHoardTermMonths)
        }
    }

    const onSaveChangeMultiple = (value?: FieldValue) => {
        const newChangeMultiple = value as O<number>
        if (newChangeMultiple && newChangeMultiple > 0 && newChangeMultiple !== changeMultipleState) {
            setChangeMultipleState(newChangeMultiple)
            submitChangeMultiple(newChangeMultiple)
        }
    }

    const onAddProduct = () => {
        toggleModal(ModalId.NON_HOARDABLE_PRODUCT_FORM)
        setCurrentProductId(undefined)
    }

    const onRemoveProduct = (productId: string) => {
        setCurrentProductId(productId)
        toggleModal(ModalId.UNIGNORE_PRODUCT)
    }

    const documentsFields: EditableFormField[] = [
        {
            name: 'document',
            type: FieldType.SELECT,
            label: 'Documento',
            disabled: true,
            render: () => (
                <SelectField
                    id="documentKey"
                    value={documentKey}
                    options={documentKeyOptions}
                    onChange={onChangeDocumentKey}
                />
            )
        },
        {
            name: 'documentFooter',
            type: FieldType.LONG_TEXT,
            value: documentFooterState,
            label: 'Pie de Página',
            hint: 'El texto se mostrará en el pie de página del documento seleccionado',
            disabled: !documentKey,
            onSave: onSaveDocumentFooter,
        }
    ]
    if (documentKey && [DocumentKey.BUDGET, DocumentKey.SALE].includes(documentKey)) {
        documentsFields.push({
            name: 'documentPaymentTypeIds',
            type: FieldType.TABLE,
            label: 'Medios de Pago Visibles',
            hint: 'Para cada lista de precio, definir qué medios de pago se muestran en el documento.',
            disabled: true,
            render: () => <DocumentPaymentTypeIdsFields
                key={documentPaymentTypeIdsFieldKey}
                documentKey={documentKey}
                documentPaymentTypeIds={documentPaymentTypeIds}
                onSaveData={submitDocumentPaymentTypeIds}
            />
        })
    }

    const hoardFields: EditableFormField[] = [
        {
            name: 'generalHoardTermMonths',
            type: FieldType.NUMBER,
            defaultValue: generalHoardTermMonthsState,
            label: 'Vigencia de Acopios',
            hint: 'Meses por defecto para la fecha límite de los nuevos acopios.',
            min: 1,
            integer: true,
            onSave: onSaveGeneralSaleYield,
        },
        {
            name: 'nonHoardableProducts',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Productos No Acopiables',
            hint: 'Agregar a la lista los productos que no deben ser acopiados.',
            disabled: true,
            render: () => (
                <NonHoardableProductsTable
                    rows={ignoredProductsData}
                    readOnly={readOnly}
                    onAdd={onAddProduct}
                    onRemoveRow={onRemoveProduct}
                />
            )
        }
    ]

    const paymentFields: EditableFormField[] = [
        {
            name: 'changeMultiple',
            type: FieldType.PRICE,
            defaultValue: changeMultipleState,
            label: 'Múltiplo de Vuelto',
            hint: 'Monto del billete más chico con el que se da vuelto. Al dar vuelto en efectivo se propone un vuelto redondeado a este monto.',
            min: 1,
            onSave: onSaveChangeMultiple,
        }
    ]

    const onUnignoreProduct = (productId: string) => {
        submitUnignoreProduct(productId)
        setCurrentProductId(undefined)
    }

    const tabs = [
        {
            id: 'documents',
            title: 'Documentos',
            content: <EditablePanel
                fields={documentsFields}
                readOnly={readOnly}
            />
        },
        {
            id: 'hoard',
            title: 'Acopios',
            content: <EditablePanel
                fields={hoardFields}
                readOnly={readOnly}
            />
        },
        {
            id: 'payments',
            title: 'Cobros',
            content: <EditablePanel
                fields={paymentFields}
                readOnly={readOnly}
            />
        },
        {
            // Mismo panel que en Configuración de Productos: comparten los datos, se modifica desde cualquiera de los dos
            id: 'negative-stock',
            title: 'Venta Sin Stock',
            content: <NegativeStockPanel />
        }
    ]

    return (
        <>
            <TabsPanel
                title="Configuración"
                tabs={tabs}
                wide
            />
            <NonHoardableProductFormModal
                filterProducts={filterProducts}
                onSubmit={submitIgnoreProduct}
            />
            <UnignoreProductModal
                productId={currentProductId}
                onSubmit={onUnignoreProduct}
            />
        </>
    )
}
